import React, { useEffect, useState } from 'react';
import { Hash, Info } from 'lucide-react';
import { VERSION_INFO, getBuildInfo } from '../../version';

interface VersionInfoProps {
  variant?: 'compact' | 'detailed' | 'footer';
  className?: string;
}

interface RuntimeVersionInfo {
  version: string;
  gitHash: string;
  gitShortHash: string;
  gitBranch: string;
  commitDate: string;
  buildDate: string;
  isDirty: boolean;
  buildNumber: string;
  environment: string;
}

export const VersionInfo: React.FC<VersionInfoProps> = ({
  variant = 'compact',
  className = ''
}) => {
  const [runtimeVersion, setRuntimeVersion] = useState<RuntimeVersionInfo | null>(null);
  const [backendVersion, setBackendVersion] = useState<string | null>(null);

  useEffect(() => {
    // Try to fetch runtime version info from the server
    fetch('/version.json')
      .then(response => response.json())
      .then(data => setRuntimeVersion(data))
      .catch(() => {
        // Fallback to build-time version info
        console.log('Using build-time version info');
      });

    // Fetch backend version from health endpoint
    fetch('/api/health')
      .then(response => response.json())
      .then(data => {
        if (data.version) {
          setBackendVersion(data.version);
        }
      })
      .catch(() => {
        console.log('Could not fetch backend version');
      });
  }, []);

  // Use runtime version if available, otherwise use build-time version
  const versionInfo = runtimeVersion || VERSION_INFO;

  // Clean version string - just show version number, no git hash or dirty flag
  const versionString = runtimeVersion
    ? `v${runtimeVersion.version}`
    : `v${VERSION_INFO.version}`;

  const buildInfo = runtimeVersion
    ? {
        buildDate: new Date(runtimeVersion.buildDate),
        branch: runtimeVersion.gitBranch === 'unknown' ? 'main' : runtimeVersion.gitBranch,
        environment: runtimeVersion.environment
      }
    : getBuildInfo();

  if (variant === 'compact') {
    return (
      <div className={`inline-flex items-center space-x-2 text-xs ${className}`}>
        <Hash className="w-3 h-3" />
        <span className="font-mono">{versionString}</span>
      </div>
    );
  }

  if (variant === 'footer') {
    return (
      <div className={`text-xs text-gray-500 ${className}`}>
        <div className="flex items-center space-x-3">
          <span className="font-mono">UI: {versionString}</span>
          {backendVersion && (
            <>
              <span>•</span>
              <span className="font-mono">API: v{backendVersion}</span>
            </>
          )}
          <span>•</span>
          <span>{buildInfo.environment}</span>
        </div>
      </div>
    );
  }

  // Detailed variant
  return (
    <div className={`bg-white rounded-lg shadow-md p-4 ${className}`}>
      <div className="flex items-center space-x-2 mb-3">
        <Info className="w-5 h-5 text-blue-600" />
        <h3 className="font-semibold text-gray-900">Version Information</h3>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Version:</span>
          <span className="font-mono font-medium">{versionString}</span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Git Hash:</span>
          <span className="font-mono text-xs">{versionInfo.gitShortHash}</span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Branch:</span>
          <span className="font-medium">{versionInfo.gitBranch}</span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Environment:</span>
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            versionInfo.environment === 'production' 
              ? 'bg-green-100 text-green-800'
              : versionInfo.environment === 'staging'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-blue-100 text-blue-800'
          }`}>
            {versionInfo.environment}
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Built:</span>
          <span className="text-xs">{buildInfo.buildDate.toLocaleString()}</span>
        </div>
        
        {versionInfo.buildNumber !== 'local' && (
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Build Number:</span>
            <span className="font-mono text-xs">{versionInfo.buildNumber}</span>
          </div>
        )}
        
        {versionInfo.isDirty && (
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Status:</span>
            <span className="text-orange-600 text-xs font-medium">Uncommitted changes</span>
          </div>
        )}
      </div>
    </div>
  );
};